/* ==========================================================================
   FEMME AURA — SIGNATURE behaviour
   Loaded deferred from layout/theme.liquid. Everything here is a progressive
   enhancement: with the file missing the store still works as plain Dawn.

   1 · Intro (logo animation) — once per browser session
   2 · Reveal on scroll
   3 · Product rails (arrows + disabled states)
   4 · Fit finder (band/cup + shapewear size from measurements)
   5 · FAQ accordion
   6 · Size guide modal (opened from the product page)
   7 · Sticky buy bar + delivery estimate
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var design = !!(window.Shopify && window.Shopify.designMode);

  root.classList.add('sg-js');

  /* --- 1 · intro ----------------------------------------------------------- */

  function intro() {
    var el = document.getElementById('SgIntro');
    if (!el) return;

    var seen = false;
    try {
      seen = sessionStorage.getItem('sg-intro') === '1';
    } catch (e) {
      seen = false; // private mode: just play it
    }

    // Skip in the theme editor, for reduced motion, and after the first view
    if (seen || reduce || design) {
      el.remove();
      root.classList.remove('sg-intro-lock');
      return;
    }

    try {
      sessionStorage.setItem('sg-intro', '1');
    } catch (e) {
      /* ignore */
    }

    var finish = function () {
      el.classList.add('is-done');
      root.classList.remove('sg-intro-lock');
      window.setTimeout(function () {
        if (el.parentNode) el.remove();
      }, 700);
    };

    // Whichever comes first: the animation finishing, the page load, or 2.2s
    var t = window.setTimeout(finish, 2200);
    el.addEventListener('click', function () {
      window.clearTimeout(t);
      finish();
    });
    document.addEventListener('keydown', function onKey(e) {
      if (e.key !== 'Escape') return;
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
      finish();
    });
  }

  /* --- 2 · reveal ---------------------------------------------------------- */

  var revealObserver = null;

  function reveals(scope) {
    var list = (scope || document).querySelectorAll('[data-sg-reveal]');
    if (!list.length) return;
    if (!('IntersectionObserver' in window) || reduce) {
      list.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            e.target.classList.add('is-in');
            revealObserver.unobserve(e.target);
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
      );
    }
    list.forEach(function (el) {
      if (!el.classList.contains('is-in')) revealObserver.observe(el);
    });
  }

  /* --- 3 · rails ----------------------------------------------------------- */

  function rails(scope) {
    (scope || document).querySelectorAll('[data-sg-rail]').forEach(function (rail) {
      if (rail.dataset.sgReady) return;
      rail.dataset.sgReady = '1';

      var track = rail.querySelector('.sg-rail__track');
      var prev = rail.querySelector('[data-sg-prev]');
      var next = rail.querySelector('[data-sg-next]');
      if (!track) return;

      var step = function () {
        var first = track.firstElementChild;
        if (!first) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      };

      var sync = function () {
        if (!prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      };

      if (prev) {
        prev.addEventListener('click', function () {
          track.scrollBy({ left: -step() * 2, behavior: reduce ? 'auto' : 'smooth' });
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          track.scrollBy({ left: step() * 2, behavior: reduce ? 'auto' : 'smooth' });
        });
      }
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  /* --- 4 · fit finder -------------------------------------------------------
     Sized for this catalogue, which sells bras by band (34, 36, 38, 40) in
     the sizing used across Pakistan and much of Asia:
       band  = underbust in cm rounded to the nearest 5, mapped 65cm→30"
               (so 70→32, 75→34, 80→36, 85→38, 90→40)
       cup   = bust minus underbust in cm, on the usual 2.5cm-per-letter steps
       shape = waist in cm against the theme's XS–XL chart
     Everything runs locally; the copy calls it a starting point, not a promise.
     ------------------------------------------------------------------------ */

  var CUP_STEPS = [
    { max: 12, cup: 'A' },
    { max: 14.5, cup: 'B' },
    { max: 17, cup: 'C' },
    { max: 19.5, cup: 'D' },
    { max: 22, cup: 'DD' },
    { max: 24.5, cup: 'E' },
    { max: 27, cup: 'F' },
  ];
  var SHAPE = [
    { max: 66, size: 'XS' },
    { max: 71, size: 'S' },
    { max: 76, size: 'M' },
    { max: 84, size: 'L' },
    { max: 92, size: 'XL' },
  ];

  function toCm(value, unit) {
    return unit === 'in' ? value * 2.54 : value;
  }

  function bandFrom(underbustCm) {
    var rounded = Math.round(underbustCm / 5) * 5;
    var clamped = Math.min(110, Math.max(65, rounded));
    return 30 + ((clamped - 65) / 5) * 2;
  }

  function cupFrom(differenceCm) {
    for (var i = 0; i < CUP_STEPS.length; i++) {
      if (differenceCm < CUP_STEPS[i].max) return CUP_STEPS[i].cup;
    }
    return 'F+';
  }

  function fitFinder() {
    var form = document.querySelector('[data-sg-fit]');
    if (!form || form.dataset.sgReady) return;
    form.dataset.sgReady = '1';

    var out = form.querySelector('[data-sg-fit-result]');
    var sizeOut = form.querySelector('[data-sg-fit-size]');
    var noteOut = form.querySelector('[data-sg-fit-note]');

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var unit = (form.querySelector('[name="unit"]:checked') || {}).value || 'cm';
      var under = parseFloat((form.querySelector('[name="underbust"]') || {}).value);
      var bust = parseFloat((form.querySelector('[name="bust"]') || {}).value);
      var waist = parseFloat((form.querySelector('[name="waist"]') || {}).value);

      var parts = [];
      var lines = [];

      if (!isNaN(under) && under > 0) {
        var underCm = toCm(under, unit);
        var band = bandFrom(underCm);
        parts.push('Band ' + band);
        if (!isNaN(bust) && bust > 0) {
          var cup = cupFrom(toCm(bust, unit) - underCm);
          parts[parts.length - 1] = 'Band ' + band + ' · Cup ' + cup;
          lines.push('Our bras are listed by band size, so look for ' + band + '.');
        } else {
          lines.push('Add your bust measurement for a cup letter too.');
        }
      }

      if (!isNaN(waist) && waist > 0) {
        var waistCm = toCm(waist, unit);
        var match = SHAPE.filter(function (s) {
          return waistCm <= s.max;
        })[0];
        var shapeSize = match ? match.size : 'XL';
        parts.push('Shapewear ' + shapeSize);
      }

      if (!parts.length) {
        parts = ['Add your measurements'];
        lines = ['Enter underbust and bust for a bra size, or waist for shapewear.'];
      } else {
        lines.push('This is a starting point — between two sizes, take the smaller one for a closer fit.');
      }

      if (sizeOut) sizeOut.textContent = parts.join('  ·  ');
      if (noteOut) noteOut.textContent = lines.join(' ');
      if (out) out.classList.add('is-shown');
    });

    form.addEventListener('reset', function () {
      if (out) out.classList.remove('is-shown');
    });
  }

  /* --- 5 · FAQ ------------------------------------------------------------- */

  function faq(scope) {
    (scope || document).querySelectorAll('[data-sg-faq] .sg-faq__q').forEach(function (btn) {
      if (btn.dataset.sgReady) return;
      btn.dataset.sgReady = '1';
      btn.addEventListener('click', function () {
        var item = btn.closest('.sg-faq__item');
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (item) item.classList.toggle('is-open', !open);
      });
    });
  }

  /* --- 6 · size guide modal ------------------------------------------------- */

  var lastFocus = null;

  function openModal(modal) {
    lastFocus = document.activeElement;
    modal.classList.add('is-open');
    modal.removeAttribute('aria-hidden');
    document.body.classList.add('overflow-hidden');
    var close = modal.querySelector('.sg-modal__close');
    if (close) close.focus();
  }

  function closeModal(modal) {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  // Put a size-guide link next to the size selector: research on intimates
  // stores is consistent that the chart has to be one click from the picker.
  function addSizeGuideLink() {
    var main = document.querySelector('#MainContent [id^="MainProduct-"]');
    if (!main) return;
    if (main.querySelector('[data-sg-sizeguide], .pdp-size-guide__button')) return;
    var legends = main.querySelectorAll('.product-form__input legend.form__label, .product-form__input > .form__label');
    for (var i = 0; i < legends.length; i++) {
      var text = (legends[i].textContent || '').toLowerCase();
      if (text.indexOf('size') === -1) continue;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sg-sizeguide-open';
      btn.setAttribute('data-sg-sizeguide', '');
      btn.textContent = 'Size guide';
      legends[i].appendChild(btn);
      return;
    }
  }

  function sizeGuide() {
    var modal = document.getElementById('SgSizeGuide');
    if (!modal) return;
    addSizeGuideLink();
    document.addEventListener('shopify:section:load', addSizeGuideLink);

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-sg-sizeguide], .pdp-size-guide__button, .fa-size-guide');
      if (opener) {
        e.preventDefault();
        openModal(modal);
        return;
      }
      if (e.target.closest('.sg-modal__close') || e.target === modal) closeModal(modal);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal(modal);
    });
  }

  /* --- 7 · sticky buy bar + delivery estimate -------------------------------- */

  function workingDaysFrom(days) {
    var d = new Date();
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      var day = d.getDay();
      if (day !== 0) added++; // Sunday off; Pakistan couriers run Mon–Sat
    }
    return d;
  }

  function deliveryEstimate() {
    var slot = document.querySelector('[data-sg-delivery]');
    if (!slot) return;
    var min = parseInt(slot.getAttribute('data-min') || '2', 10);
    var max = parseInt(slot.getAttribute('data-max') || '4', 10);
    var fmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    slot.textContent = fmt.format(workingDaysFrom(min)) + ' – ' + fmt.format(workingDaysFrom(max));
  }

  function stickyBar() {
    var bar = document.querySelector('[data-sg-sticky]');
    if (!bar) return;
    var main = document.querySelector('#MainContent [id^="MainProduct-"]') || document.getElementById('MainContent');
    var mainBtn = main && main.querySelector('product-form .product-form__submit, .product-form__submit');
    if (!mainBtn) return;

    var btn = bar.querySelector('[data-sg-sticky-btn]');
    var priceOut = bar.querySelector('[data-sg-sticky-price]');
    var labelOut = btn && btn.querySelector('[data-sg-sticky-label]');

    var sync = function () {
      var priceWrap = main.querySelector('.price');
      if (priceWrap && priceOut) {
        var onSale = priceWrap.classList.contains('price--on-sale');
        var item = onSale
          ? priceWrap.querySelector('.price__sale .price-item--last')
          : priceWrap.querySelector('.price__regular .price-item--regular');
        if (item && item.textContent.trim()) priceOut.textContent = item.textContent.trim();
      }
      if (labelOut) {
        var disabled = mainBtn.disabled || mainBtn.getAttribute('aria-disabled') === 'true';
        var mainLabel = mainBtn.querySelector('span');
        labelOut.textContent = disabled
          ? bar.getAttribute('data-choose') || 'Choose size'
          : (mainLabel && mainLabel.textContent.trim()) || 'Add to cart';
      }
    };
    sync();
    new MutationObserver(sync).observe(main, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'class'],
    });

    if (btn) {
      btn.addEventListener('click', function () {
        var disabled = mainBtn.disabled || mainBtn.getAttribute('aria-disabled') === 'true';
        if (disabled) {
          var picker = main.querySelector('variant-selects, .product-form__input') || mainBtn;
          var y = picker.getBoundingClientRect().top + window.scrollY - 140;
          window.scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' });
          return;
        }
        mainBtn.click();
      });
    }

    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(
      function (entries) {
        var e = entries[0];
        var show = !e.isIntersecting && e.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (btn) btn.tabIndex = show ? 0 : -1;
        document.body.classList.toggle('sg-sticky-on', show);
      },
      { threshold: 0 }
    ).observe(mainBtn);
  }

  /* --- 8 · product page extras ---------------------------------------------
     The delivery / payment / returns rows live in a <template> inside the
     sticky-bar section, because the product section itself is a 138 KB
     customised file we do not want to fork. Move them under the buy buttons.
     ------------------------------------------------------------------------ */

  function pdpExtra() {
    var tpl = document.querySelector('[data-sg-pdp-extra-template]');
    if (!tpl || !tpl.content) return;
    var main = document.querySelector('#MainContent [id^="MainProduct-"]');
    if (!main || main.querySelector('.sg-pdp-extra')) return;
    var anchor =
      main.querySelector('.product-form__buttons') ||
      main.querySelector('product-form') ||
      main.querySelector('.product-form');
    if (!anchor || !anchor.parentNode) return;
    anchor.parentNode.insertBefore(tpl.content.cloneNode(true), anchor.nextSibling);
  }

  /* --- 9 · mega menu ---------------------------------------------------------
     sg-mega.liquid renders one panel per top-level menu item. Here we wire the
     existing header links to those panels on pointer devices, and suppress
     Dawn's own dropdown so the two never show at once. Touch and small screens
     keep Dawn's drawer / dropdown untouched.
     ------------------------------------------------------------------------ */

  var megaTimer = null;

  function megaMenu() {
    var wrap = document.querySelector('[data-sg-mega]');
    if (!wrap) return;
    var fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    var wide = window.matchMedia('(min-width: 990px)');

    var panels = {};
    wrap.querySelectorAll('[data-sg-mega-panel]').forEach(function (p) {
      panels[p.getAttribute('data-sg-mega-panel')] = p;
    });

    var open = null;

    var show = function (key) {
      var panel = panels[key];
      if (!panel || panel === open) return;
      hide(true);
      panel.classList.add('is-open');
      wrap.classList.add('is-open');
      open = panel;
      var trigger = document.querySelector('[data-sg-mega-trigger="' + key + '"]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    };

    var hide = function (immediate) {
      if (!open) return;
      open.classList.remove('is-open');
      var key = open.getAttribute('data-sg-mega-panel');
      var trigger = document.querySelector('[data-sg-mega-trigger="' + key + '"]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      open = null;
      if (!immediate) wrap.classList.remove('is-open');
      else wrap.classList.remove('is-open');
    };

    var canUse = function () {
      return fine.matches && wide.matches;
    };

    // Match header links to panels by their handle
    document.querySelectorAll('.header__inline-menu > ul > li').forEach(function (li) {
      var link = li.querySelector('a, summary');
      if (!link) return;
      // Shopify's `handle` filter drops apostrophes rather than hyphenating
      // them, so "Bra's" becomes "bras". Match that exactly or nothing pairs up.
      var label = (link.textContent || '').trim().toLowerCase();
      var key = label
        .replace(/[‘’'"]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      if (!panels[key]) return;

      li.setAttribute('data-sg-mega-trigger', key);
      li.classList.add('sg-has-mega');
      link.setAttribute('aria-expanded', 'false');

      li.addEventListener('mouseenter', function () {
        if (!canUse()) return;
        window.clearTimeout(megaTimer);
        show(key);
      });
      li.addEventListener('mouseleave', function () {
        if (!canUse()) return;
        megaTimer = window.setTimeout(function () {
          hide();
        }, 160);
      });
      li.addEventListener('focusin', function () {
        if (!canUse()) return;
        show(key);
      });
    });

    wrap.addEventListener('mouseenter', function () {
      window.clearTimeout(megaTimer);
    });
    wrap.addEventListener('mouseleave', function () {
      megaTimer = window.setTimeout(function () {
        hide();
      }, 160);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') hide(true);
    });
    document.addEventListener('focusin', function (e) {
      if (!open) return;
      if (wrap.contains(e.target)) return;
      if (e.target.closest('[data-sg-mega-trigger]')) return;
      hide(true);
    });
    window.addEventListener('scroll', function () {
      if (open && window.scrollY > 260) hide(true);
    }, { passive: true });
  }

  /* --- boot ----------------------------------------------------------------- */

  function init() {
    intro();
    reveals();
    rails();
    fitFinder();
    faq();
    sizeGuide();
    pdpExtra();
    deliveryEstimate();
    stickyBar();
    megaMenu();

    document.addEventListener('shopify:section:load', function (e) {
      reveals(e.target);
      rails(e.target);
      faq(e.target);
      fitFinder();
      deliveryEstimate();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
